import { theme } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { ArticleRenderChildProps } from "~/components/article/article-render";
import { GlobalChecker } from "~/components/article/article-render/global-checker";
import { ClientSideSuspense } from "~/components/common/client-side-suspense";
import { defineInlineAdSlotSchema } from "~/components/content/content-schema-render/inline-ad-slot/schemas";
import type { morningStudioArticleContent$key } from "~/queries/__generated__/morningStudioArticleContent.graphql";

import {
  Author,
  Body,
  Correction,
  Disclaimer,
  FooterAdSlot,
  FooterAdSlotMobile,
  HeaderAdSlotDesktop,
  HeaderAdSlotMobile,
  MorningStudioArticleContainer,
  Social,
  StyledMorningStudioHeader,
  SubHeadline,
  Topics,
} from "./styles";

type Props = {
  reference: morningStudioArticleContent$key;
} & ArticleRenderChildProps;

export const MorningStudioArticle: FunctionComponent<Props> = ({
  provider,
  reference: reference_,
}) => {
  const content = useFragment(
    graphql`
      fragment morningStudioArticleContent on Content {
        ...globalCheckerContent
        ...headerMorningStudioArticle
      }
    `,
    reference_,
  );

  return (
    <MorningStudioArticleContainer>
      <StyledMorningStudioHeader reference={content}>
        {provider.leading({
          responsiveDescriptionVariants: {
            desktopUp: "hidden",
            mobileUp: "hidden",
            tabletUp: "hidden",
          },
        })}
      </StyledMorningStudioHeader>

      <HeaderAdSlotDesktop>
        {provider.adslot({
          adUnit: "d_banner1",
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [[970, 250], [728, 90], [970, 90], "fluid"],
        })}
      </HeaderAdSlotDesktop>

      <HeaderAdSlotMobile>
        {provider.adslot({
          adUnit: "m_banner1",
          breakpoint: theme.breakpoints.only("mobile"),
          sizes: [[300, 250], "fluid"],
          withLabel: true,
        })}
      </HeaderAdSlotMobile>

      <SubHeadline>{provider.subHeadline()}</SubHeadline>
      <Author>{provider.authorBlock()}</Author>
      <Correction>
        {provider.correction({
          variant: "dark",
        })}
      </Correction>
      <Body>
        {provider.body({
          extraComponentMap: {
            "inline-widget": null,
          },
          inlineAdSlots: {
            "0": {
              adUnit: "outstream1",
            },
            morning_studio_1: {
              adUnit: "m_lrec1",
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
              withBackground: true,
              withLabel: true,
              withSticky: true,
            },
            morning_studio_2: {
              adUnit: "d_lrec2",
              sizes: [[300, 250], [300, 600], [320, 480], "fluid"],
              withBackground: true,
              withLabel: true,
              withSticky: true,
            },
          },
          preprocessSchema(schema) {
            const pageSpecificCustomContents = [
              defineInlineAdSlotSchema({ row: 5, tag: "morning_studio_1" }),
              defineInlineAdSlotSchema({ row: 10, tag: "morning_studio_2" }),
            ].sort((a, b) => a.row - b.row);

            // this ad-slot position finding logic is different from the one in content service
            // as advised by the PM, we use a different logic only for morning studio articles
            // https://scmp.slack.com/archives/C02S11PDGMD/p1679475941818559
            // https://project.scmp.tech/product/web/scmp-pwa/-/blob/1deee0c7d5e467f4a37fe08aba52f77b77e0d1af/scmp-app/src/universal/components/article/Studio/index.vue#L720
            const findAdSlotPosition = (schema: Schema, position: number) => {
              let slotCount = 0;
              return schema.findIndex(node => {
                if (node.type === "p" && node.children?.[0]?.type === "text") slotCount++;
                return slotCount >= position;
              });
            };
            // in pwa v1 codebase, https://project.scmp.tech/product/web/scmp-pwa/-/blob/1deee0c7d5e467f4a37fe08aba52f77b77e0d1af/scmp-app/src/universal/components/article/Studio/index.vue#L109
            // the ad slot position is rendered together with the paragraph. therefore, no need to +1
            // but in pwa v2 codebase, we insert the ad slot into the schema as an array element,
            // the ad slot is not rendered together with the paragraph. So, we add to +1 to the adSlotPosition
            // to make sure the ad slot is just after the desired paragraph.
            const newSchema = [...schema];
            pageSpecificCustomContents.forEach(customContent => {
              const position = findAdSlotPosition(newSchema, customContent.row);
              if (position >= 0) newSchema.splice(position + 1, 0, customContent);
            });
            return newSchema;
          },
        })}
      </Body>

      <ClientSideSuspense>
        <GlobalChecker reference={content}>
          <FooterAdSlot>
            {provider.adslot({
              adUnit: "native4",
              breakpoint: "@media all", // always show
              sizes: ["fluid"],
            })}
          </FooterAdSlot>

          <Topics>{provider.topics()}</Topics>

          <Social>
            {provider.shareWidget({
              variant: "morningStudio",
            })}
          </Social>

          <FooterAdSlotMobile>
            {provider.adslot({
              adUnit: "m_lrec3a",
              breakpoint: theme.breakpoints.only("mobile"),
              sizes: [[300, 250], "fluid"],
              withBackground: true,
              withLabel: true,
            })}
          </FooterAdSlotMobile>

          <Disclaimer>
            The news and editorial staffs of South China Morning Post had no role in this
            post&apos;s preparation.
          </Disclaimer>
        </GlobalChecker>
      </ClientSideSuspense>
    </MorningStudioArticleContainer>
  );
};
